import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Phone,
  Mail,
  MessageSquare,
  Calendar,
  FileText,
  ArrowRight,
  Plus,
  Clock,
  User,
} from 'lucide-react';
import {
  ClientActivity,
  ActivityType,
  ACTIVITY_TYPES,
  LIFECYCLE_STAGES,
  ClientLifecycleStage,
} from '@/types/crm';
import { projectAPI } from '@/utils/supabase/client';

interface ClientActivityTabProps {
  clientId: string;
  activities: ClientActivity[];
  onActivityAdded: () => void;
}

// Icon mapping for activity types
const ACTIVITY_ICONS: Record<ActivityType, React.ElementType> = {
  call: Phone,
  email: Mail,
  text: MessageSquare,
  meeting: Calendar,
  note: FileText,
};

export function ClientActivityTab({ clientId, activities, onActivityAdded }: ClientActivityTabProps) {
  const [activityType, setActivityType] = useState<ActivityType>('note');
  const [content, setContent] = useState('');
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const handleAddActivity = async () => {
    if (!content.trim()) return;

    setSaving(true);
    try {
      await projectAPI.addClientActivity(clientId, {
        activity_type: activityType,
        content: content.trim(),
      });
      setContent('');
      setShowForm(false);
      onActivityAdded();
    } catch (error) {
      console.error('Failed to add activity:', error);
    } finally {
      setSaving(false);
    }
  };

  // Empty state
  if (activities.length === 0 && !showForm) {
    return (
      <div className="space-y-4">
        <Button
          variant="outline"
          className="w-full"
          onClick={() => setShowForm(true)}
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Activity
        </Button>
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            <Clock className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No activities recorded yet.</p>
            <p className="text-sm mt-2">
              Add notes, calls, emails, and meetings to track client interactions.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Add Activity Button / Form */}
      {showForm ? (
        <Card>
          <CardContent className="pt-4 space-y-4">
            <div className="space-y-2">
              <Label>Activity Type</Label>
              <Select value={activityType} onValueChange={(v) => setActivityType(v as ActivityType)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(ACTIVITY_TYPES).map(([key, { label }]) => {
                    const Icon = ACTIVITY_ICONS[key as ActivityType];
                    return (
                      <SelectItem key={key} value={key}>
                        <div className="flex items-center gap-2">
                          <Icon className="h-4 w-4" />
                          {label}
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Content</Label>
              <Textarea
                placeholder="Enter activity details..."
                value={content}
                onChange={(e) => setContent(e.target.value)}
                rows={3}
              />
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setShowForm(false);
                  setContent('');
                }}
              >
                Cancel
              </Button>
              <Button
                className="flex-1"
                onClick={handleAddActivity}
                disabled={saving || !content.trim()}
              >
                {saving ? 'Saving...' : 'Add Activity'}
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Button
          variant="outline"
          className="w-full"
          onClick={() => setShowForm(true)}
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Activity
        </Button>
      )}

      {/* Activity Timeline */}
      {activities.length > 0 && (
        <ScrollArea className="min-h-[200px] max-h-[50vh]">
          <div className="space-y-1 pr-4">
            {activities.map((activity, index) => (
              <ActivityEntry
                key={activity.id}
                activity={activity}
                isLast={index === activities.length - 1}
              />
            ))}
          </div>
        </ScrollArea>
      )}
    </div>
  );
}

function ActivityEntry({ activity, isLast }: { activity: ClientActivity; isLast: boolean }) {
  const Icon = ACTIVITY_ICONS[activity.activity_type];
  const isStageTransition = activity.from_stage && activity.to_stage;

  return (
    <div className={`relative pl-6 pb-4 ${!isLast ? 'border-l-2 border-muted ml-1.5' : 'ml-1.5'}`}>
      {/* Timeline dot */}
      <div className="absolute -left-1.5 top-0 h-3 w-3 rounded-full bg-primary" />

      <div className="space-y-1">
        {/* Header: date, user, type */}
        <div className="flex items-center gap-2 flex-wrap">
          <Badge variant="outline" className="text-xs flex items-center gap-1">
            <Icon className="h-3 w-3" />
            {ACTIVITY_TYPES[activity.activity_type].label}
          </Badge>
          {isStageTransition && (
            <Badge variant="secondary" className="text-xs flex items-center gap-1">
              {LIFECYCLE_STAGES[activity.from_stage as ClientLifecycleStage]?.label}
              <ArrowRight className="h-3 w-3" />
              {LIFECYCLE_STAGES[activity.to_stage as ClientLifecycleStage]?.label}
            </Badge>
          )}
          <span className="text-xs text-muted-foreground">
            {formatDate(activity.created_at)}
          </span>
          {activity.created_by && (
            <>
              <span className="text-xs text-muted-foreground">•</span>
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                <User className="h-3 w-3" />
                {extractUsername(activity.created_by)}
              </span>
            </>
          )}
        </div>

        {/* Content */}
        <p className="text-sm">{activity.content}</p>
      </div>
    </div>
  );
}

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
  });
}

function extractUsername(email: string): string {
  return email.split('@')[0];
}
